import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Trophy, TrendingUp, ListTodo, ClipboardCheck, Briefcase, Award, BarChart3 } from "lucide-react";

const TIER = (s) => {
  if (s >= 80) return { label: "Excellent", color: "bg-green-50 text-green-800 border-green-300" };
  if (s >= 60) return { label: "Good", color: "bg-blue-50 text-blue-800 border-blue-300" };
  if (s >= 40) return { label: "Average", color: "bg-amber-50 text-amber-800 border-amber-300" };
  return { label: "Needs focus", color: "bg-red-50 text-red-800 border-red-300" };
};

export default function MIS() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/mis/performance").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-10 label-uppercase">Loading…</div>;

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen" data-testid="mis-page">
      <header className="border-b border-zinc-200 page-px py-6 sm:py-8">
        <div className="label-uppercase mb-2">Insights</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter flex items-center gap-3" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
          <BarChart3 size={28} className="text-[#002FA7]" /> Performance MIS
        </h1>
        <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Last {data.period_days} days · {isAdmin ? "Team Overview" : "My Performance"}</p>
      </header>

      {isAdmin ? (
        <AdminView employees={data.employees} />
      ) : (
        <EmployeeView metrics={data.self} />
      )}
    </div>
  );
}

function AdminView({ employees }) {
  const top = employees[0];
  const totalTasks = employees.reduce((a, e) => a + e.tasks.total, 0);
  const totalDone = employees.reduce((a, e) => a + e.tasks.done, 0);
  const teamCompletion = totalTasks ? Math.round((totalDone / totalTasks) * 100) : 0;
  const totalDays = employees.reduce((a, e) => a + e.attendance.days_present, 0);
  const totalLeadsWon = employees.reduce((a, e) => a + e.leads.won, 0);

  return (
    <>
      {/* Team summary */}
      <section className="grid grid-cols-2 lg:grid-cols-4 border-b border-zinc-200">
        <Stat label="Team Completion" value={`${teamCompletion}%`} icon={TrendingUp} />
        <Stat label="Tasks Done" value={`${totalDone}/${totalTasks}`} icon={ListTodo} />
        <Stat label="Days Present" value={totalDays} icon={ClipboardCheck} />
        <Stat label="Leads Won" value={totalLeadsWon} icon={Briefcase} />
      </section>

      {/* Leaderboard */}
      <section className="page-px py-6 sm:py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium tracking-tight flex items-center gap-2">
            <Trophy size={16} className="text-amber-500" /> Leaderboard
          </h2>
          {top && (
            <div className="text-xs text-zinc-500">
              <span className="label-uppercase mr-2">Top performer</span>
              <span className="font-semibold text-zinc-900">{top.name}</span>
            </div>
          )}
        </div>

        <div className="border border-zinc-200 overflow-x-auto">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Employee</div>
              <div className="col-span-2">Tasks (Done/Total)</div>
              <div className="col-span-2">Completion</div>
              <div className="col-span-2">Attendance</div>
              <div className="col-span-1">Leads Won</div>
              <div className="col-span-1 text-right">Score</div>
            </div>
            {employees.length === 0 && (
              <div className="px-4 py-12 text-sm text-zinc-500 text-center">No employees yet.</div>
            )}
            {employees.map((e, idx) => {
              const tier = TIER(e.score);
              return (
                <Link
                  to={`/users/${e.user_id}`}
                  key={e.user_id}
                  data-testid={`mis-row-${e.user_id}`}
                  className="grid grid-cols-12 px-4 py-3 border-b border-zinc-200 text-sm hover:bg-zinc-50 items-center"
                >
                  <div className="col-span-1 font-mono text-zinc-500">
                    {idx === 0 && <span className="text-amber-500 mr-1">★</span>}
                    {idx + 1}
                  </div>
                  <div className="col-span-3 font-medium">{e.name}</div>
                  <div className="col-span-2 font-mono text-xs">
                    {e.tasks.done} / {e.tasks.total}
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-zinc-100 h-1.5">
                        <div className="h-full bg-ikb" style={{ width: `${e.tasks.completion_rate}%` }} />
                      </div>
                      <span className="text-xs font-mono w-9">{e.tasks.completion_rate}%</span>
                    </div>
                  </div>
                  <div className="col-span-2 font-mono text-xs">
                    {e.attendance.days_present}d · {e.attendance.avg_hours}h avg
                  </div>
                  <div className="col-span-1 font-mono">{e.leads.won}</div>
                  <div className="col-span-1 text-right">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${tier.color}`}>
                      {e.score} · {tier.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

function EmployeeView({ metrics }) {
  const tier = TIER(metrics.score);
  return (
    <>
      <section className="grid lg:grid-cols-12 border-b border-zinc-200">
        <div className="lg:col-span-4 lg:border-r border-b lg:border-b-0 border-zinc-200 p-6 sm:p-10 flex flex-col items-center justify-center text-center">
          <div className="label-uppercase mb-3">Performance Score</div>
          <div className="relative w-40 h-40 mb-4">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#e4e4e7" strokeWidth="10" />
              <circle
                cx="60" cy="60" r="50" fill="none" stroke="#002FA7" strokeWidth="10"
                strokeDasharray={`${(metrics.score / 100) * 314} 314`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-5xl font-bold tracking-tighter font-mono">{metrics.score}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">/ 100</div>
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-3 py-1 border ${tier.color}`}>
            <Award size={10} className="inline mr-1" /> {tier.label}
          </span>
        </div>

        <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-3">
          <Stat label="Tasks Done" value={`${metrics.tasks.done}/${metrics.tasks.total}`} icon={ListTodo} />
          <Stat label="Completion" value={`${metrics.tasks.completion_rate}%`} icon={TrendingUp} />
          <Stat label="In Progress" value={metrics.tasks.in_progress} icon={ListTodo} />
          <Stat label="Days Present" value={metrics.attendance.days_present} icon={ClipboardCheck} />
          <Stat label="Avg Hours/Day" value={metrics.attendance.avg_hours} icon={ClipboardCheck} />
          <Stat label="Leads Won" value={metrics.leads.won} icon={Briefcase} />
        </div>
      </section>

      <section className="page-px py-6 sm:py-8 grid lg:grid-cols-2 gap-6">
        <Breakdown
          title="Task Breakdown"
          rows={[
            { label: "Done", value: metrics.tasks.done, color: "bg-green-500" },
            { label: "In Progress", value: metrics.tasks.in_progress, color: "bg-blue-500" },
            { label: "Todo", value: metrics.tasks.todo, color: "bg-amber-500" },
          ]}
        />
        <Breakdown
          title="Lead Breakdown"
          rows={[
            { label: "Won", value: metrics.leads.won, color: "bg-green-500" },
            { label: "Lost", value: metrics.leads.lost, color: "bg-red-500" },
            { label: "Active", value: Math.max(0, metrics.leads.assigned - metrics.leads.won - metrics.leads.lost), color: "bg-zinc-400" },
          ]}
        />
      </section>
    </>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="p-6 sm:p-8 border-l first:border-l-0 border-zinc-200">
      <div className="flex items-center justify-between mb-3">
        <span className="label-uppercase">{label}</span>
        <Icon size={13} className="text-zinc-400" strokeWidth={1.5} />
      </div>
      <div className="text-3xl font-bold tracking-tighter font-mono">{value}</div>
    </div>
  );
}

function Breakdown({ title, rows }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div>
      <h3 className="text-base font-medium tracking-tight mb-3">{title}</h3>
      <div className="space-y-2 border border-zinc-200 p-4">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3">
            <div className="w-24 label-uppercase">{r.label}</div>
            <div className="flex-1 bg-zinc-100 h-5 relative">
              <div className={`h-full ${r.color}`} style={{ width: `${(r.value / max) * 100}%` }} />
            </div>
            <div className="w-8 text-right font-mono text-sm">{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
